import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Grid,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Store as StoreIcon,
  AccountBox as AccountIcon,
  Payment as PaymentIcon,
  LocalOffer as PackageIcon,
  Business as BusinessIcon
} from '@mui/icons-material';

interface CompleteSetupStepProps {
  data: any;
  isSubmitting: boolean;
}

const CompleteSetupStep: React.FC<CompleteSetupStepProps> = ({
  data,
  isSubmitting
}) => {
  // Generate summary items based on the provided data
  const summaryItems = [
    {
      title: 'Account Information',
      icon: <AccountIcon color="primary" />,
      items: [
        { label: 'Name', value: data.name },
        { label: 'Email', value: data.email },
        { label: 'Phone', value: data.phone || 'Not provided' },
        { label: 'Account Type', value: data.accountType === 'business' ? 'Business Account' : 'Individual Account' }
      ]
    },
    {
      title: 'Business Information',
      icon: <BusinessIcon color="primary" />,
      items: [
        { label: 'Business Name', value: data.businessName },
        { label: 'Business Type', value: data.businessType },
        { label: 'Address', value: `${data.businessAddress.street}, ${data.businessAddress.city}, ${data.businessAddress.state} ${data.businessAddress.zip}` },
        { label: 'Tax ID', value: data.taxId || 'Not provided' }
      ]
    },
    {
      title: 'Payment Processing',
      icon: <PaymentIcon color="primary" />,
      items: [
        { 
          label: 'Stripe Connect', 
          value: data.stripeConnected ? 'Connected' : data.stripeSetupSkipped ? 'Setup later' : 'Not connected'
        },
        { 
          label: 'Platform Fee', 
          value: `${data.commissionRate}%` 
        }
      ]
    },
    {
      title: 'Venue Details',
      icon: <StoreIcon color="primary" />,
      items: [
        { label: 'Venue Name', value: data.venueName || data.businessName },
        { label: 'Venue Type', value: data.venueType || data.businessType },
        { label: 'Photos', value: `${data.venueImages?.length || 0} uploaded` }
      ]
    }
  ];

  // Features list
  const features = [
    {
      icon: <StoreIcon color="primary" />,
      title: 'Venue Profile',
      description: 'Customize your venue profile with photos, descriptions, and amenities.'
    },
    {
      icon: <PackageIcon color="primary" />,
      title: 'Create Packages',
      description: 'Create and manage date packages with detailed information and pricing.'
    },
    {
      icon: <PaymentIcon color="primary" />,
      title: 'Accept Payments',
      description: 'Securely process payments and manage bookings from customers.'
    }
  ];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review & Complete Setup
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review your venue account information before finalizing the setup. After creating your account, you'll be able to add more details and create packages.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Account Summary
            </Typography>
            
            {summaryItems.map((section, sectionIndex) => (
              <Box key={sectionIndex} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {section.icon}
                  <Typography variant="subtitle2" sx={{ ml: 1 }}>
                    {section.title}
                  </Typography>
                </Box>
                
                <List dense>
                  {section.items.map((item, itemIndex) => (
                    <ListItem key={itemIndex}>
                      <ListItemText 
                        primary={item.label} 
                        secondary={item.value} 
                        primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                        secondaryTypographyProps={{ variant: 'body1' }}
                      />
                    </ListItem>
                  ))}
                </List>
                
                {sectionIndex < summaryItems.length - 1 && <Divider sx={{ my: 1 }} />}
              </Box>
            ))}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={5}>
          <Paper variant="outlined" sx={{ p: 3, height: '100%' }}>
            <Typography variant="subtitle1" gutterBottom>
              What's Next
            </Typography>
            
            <Typography variant="body2" paragraph>
              After creating your venue account, you'll have access to the following features:
            </Typography>
            
            <List>
              {features.map((feature, index) => (
                <ListItem key={index} alignItems="flex-start">
                  <ListItemIcon>
                    {feature.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={feature.title}
                    secondary={feature.description}
                  />
                </ListItem>
              ))}
            </List>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="body2" color="text.secondary">
              By creating a venue account, you agree to our Terms of Service and Privacy Policy for venue partners.
            </Typography>
          </Paper>
        </Grid>
        
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {isSubmitting ? (
              <CircularProgress size={24} sx={{ mr: 1 }} />
            ) : (
              <CheckIcon color="success" sx={{ mr: 1 }} />
            )}
            <Typography variant="body1" color={isSubmitting ? 'text.secondary' : 'success.main'}>
              {isSubmitting ? 'Creating your venue account...' : 'Ready to create your venue account'}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompleteSetupStep;
